import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

var API = process.env.REACT_APP_API_URL || 'https://ivision-crm-backend-production.up.railway.app';

function CustomerPortal() {
  var { subdomain } = useParams();
  var [token, setToken] = useState(localStorage.getItem('portal_token_' + subdomain) || '');
  var [contact, setContact] = useState(null);
  var [business, setBusiness] = useState(null);
  var [tab, setTab] = useState('appointments');
  var [loading, setLoading] = useState(true);

  // Login state
  var [loginStep, setLoginStep] = useState('email');
  var [email, setEmail] = useState('');
  var [code, setCode] = useState('');
  var [loginLoading, setLoginLoading] = useState(false);
  var [loginError, setLoginError] = useState('');
  var [loginMessage, setLoginMessage] = useState('');

  // Data state
  var [appointments, setAppointments] = useState({ upcoming: [], past: [] });
  var [workOrders, setWorkOrders] = useState([]);
  var [invoices, setInvoices] = useState([]);
  var [transactions, setTransactions] = useState([]);

  // Profile edit
  var [editing, setEditing] = useState(false);
  var [profileForm, setProfileForm] = useState({});

  var accent = business?.primary_color || '#ff6b2b';

  useEffect(function() {
    if (token) loadProfile();
    else setLoading(false);
  }, [token]);

  var portalFetch = async function(path) {
    var res = await fetch(API + path, { headers: { 'Authorization': 'Bearer ' + token } });
    if (res.status === 401) { logout(); throw new Error('Session expired'); }
    return res.json();
  };

  var loadProfile = async function() {
    try {
      var data = await portalFetch('/api/customer-portal/me');
      if (data.success) {
        setContact(data.contact);
        setBusiness(data.business);
        setProfileForm({ first_name: data.contact.first_name, last_name: data.contact.last_name, phone: data.contact.phone || '', email: data.contact.email || '' });
        loadAllData();
      } else { logout(); }
    } catch (e) { logout(); }
    setLoading(false);
  };

  var loadAllData = async function() {
    try {
      var [apptData, woData, invData, txData] = await Promise.all([
        portalFetch('/api/customer-portal/appointments'),
        portalFetch('/api/customer-portal/work-orders').catch(function() { return { work_orders: [] }; }),
        portalFetch('/api/customer-portal/invoices').catch(function() { return { invoices: [] }; }),
        portalFetch('/api/customer-portal/transactions').catch(function() { return { transactions: [] }; }),
      ]);
      if (apptData.success) setAppointments({ upcoming: apptData.upcoming || [], past: apptData.past || [] });
      setWorkOrders(woData.work_orders || []);
      setInvoices(invData.invoices || []);
      setTransactions(txData.transactions || []);
    } catch (e) { console.error('Load data error:', e); }
  };

  var logout = function() {
    localStorage.removeItem('portal_token_' + subdomain);
    setToken('');
    setContact(null);
    setBusiness(null);
    setLoginStep('email');
    setEmail('');
    setCode('');
  };

  var sendCode = async function(e) {
    e.preventDefault();
    setLoginLoading(true); setLoginError('');
    try {
      var res = await fetch(API + '/api/customer-portal/send-code', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email, subdomain: subdomain }),
      });
      var data = await res.json();
      if (data.success) { setLoginStep('code'); setLoginMessage('We sent a 6-digit code to ' + email); }
      else { setLoginError(data.error || 'Something went wrong'); }
    } catch (e) { setLoginError('Network error. Please try again.'); }
    setLoginLoading(false);
  };

  var verifyCode = async function(e) {
    e.preventDefault();
    setLoginLoading(true); setLoginError('');
    try {
      var res = await fetch(API + '/api/customer-portal/verify-code', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email, code: code, subdomain: subdomain }),
      });
      var data = await res.json();
      if (data.success) {
        localStorage.setItem('portal_token_' + subdomain, data.token);
        setToken(data.token);
        setContact(data.contact);
        setBusiness(data.business);
      } else { setLoginError(data.error || 'Invalid code'); }
    } catch (e) { setLoginError('Network error. Please try again.'); }
    setLoginLoading(false);
  };

  var updateProfile = async function(e) {
    e.preventDefault();
    try {
      var res = await fetch(API + '/api/customer-portal/me', {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify(profileForm),
      });
      var data = await res.json();
      if (data.success) { setContact(data.contact); setEditing(false); }
    } catch (e) { console.error(e); }
  };

  var formatDate = function(d) {
    if (!d) return '';
    var date = new Date(d + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  var formatTime = function(t) {
    if (!t) return '';
    var parts = t.split(':').map(Number);
    var h = parts[0] > 12 ? parts[0] - 12 : parts[0] === 0 ? 12 : parts[0];
    return h + ':' + String(parts[1]).padStart(2, '0') + (parts[0] >= 12 ? ' PM' : ' AM');
  };

  var formatCurrency = function(v) { return '$' + Number(v || 0).toFixed(2); };

  var statusColor = function(s) {
    var colors = { confirmed: '#00b894', completed: '#00b894', pending: '#ffab40', cancelled: '#e74c3c', 'no-show': '#e74c3c',
      received: '#6c5ce7', diagnosed: '#0984e3', approved: '#00b894', in_progress: '#ff6b2b', qc: '#fdcb6e', ready: '#00b894',
      paid: '#00b894', partial: '#ffab40', unpaid: '#e74c3c', overdue: '#e74c3c', sent: '#0984e3', draft: '#888' };
    return colors[(s || '').toLowerCase()] || '#888';
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#888', fontSize: 18 }}>Loading...</div>
      </div>
    );
  }

  // --- LOGIN SCREEN ---
  if (!token || !contact) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8f9fa 0%, #e8ecf1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 20, padding: '40px 32px', boxShadow: '0 8px 40px rgba(0,0,0,0.08)' }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ width: 56, height: 56, borderRadius: 14, background: accent + '15', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 12 }}>👤</div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#333', margin: '0 0 4px' }}>Customer Portal</h1>
            <p style={{ color: '#888', fontSize: 14, margin: 0 }}>View your appointments, orders & invoices</p>
          </div>

          {loginError && (
            <div style={{ background: '#fee', border: '1px solid #fcc', borderRadius: 10, padding: '10px 14px', marginBottom: 16, color: '#c33', fontSize: 13, fontWeight: 600 }}>{loginError}</div>
          )}

          {loginStep === 'email' && (
            <form onSubmit={sendCode}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 6 }}>Email Address</label>
              <input type="email" value={email} onChange={function(e) { setEmail(e.target.value); }} required placeholder="you@example.com"
                style={{ width: '100%', padding: '14px 16px', border: '2px solid #e8e8ee', borderRadius: 12, fontSize: 15, outline: 'none', boxSizing: 'border-box', marginBottom: 20 }} />
              <button type="submit" disabled={loginLoading}
                style={{ width: '100%', padding: '14px', background: accent, color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: loginLoading ? 0.6 : 1 }}>
                {loginLoading ? 'Sending...' : 'Send Login Code'}
              </button>
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <Link to={'/book/' + subdomain} style={{ color: accent, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>Book an appointment →</Link>
              </div>
            </form>
          )}

          {loginStep === 'code' && (
            <form onSubmit={verifyCode}>
              {loginMessage && (
                <div style={{ background: '#f0faf5', border: '1px solid #b8e6d0', borderRadius: 10, padding: '10px 14px', marginBottom: 16, color: '#1a7a4c', fontSize: 13, fontWeight: 600 }}>{loginMessage}</div>
              )}
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 6 }}>6-Digit Code</label>
              <input type="text" value={code} onChange={function(e) { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); }} required
                placeholder="000000" maxLength={6} autoComplete="one-time-code" inputMode="numeric"
                style={{ width: '100%', padding: '14px 16px', border: '2px solid #e8e8ee', borderRadius: 12, fontSize: 24, fontWeight: 700, letterSpacing: 8, textAlign: 'center', outline: 'none', boxSizing: 'border-box', marginBottom: 20 }} />
              <button type="submit" disabled={loginLoading || code.length < 6}
                style={{ width: '100%', padding: '14px', background: accent, color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: (loginLoading || code.length < 6) ? 0.6 : 1 }}>
                {loginLoading ? 'Verifying...' : 'Sign In'}
              </button>
              <button type="button" onClick={function() { setLoginStep('email'); setCode(''); setLoginError(''); setLoginMessage(''); }}
                style={{ width: '100%', marginTop: 10, padding: '10px', background: 'none', border: 'none', color: '#888', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                ← Use different email
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // --- PORTAL DASHBOARD ---
  var tabs = [
    { id: 'appointments', label: 'Appointments', icon: '📅', count: appointments.upcoming.length },
    { id: 'work-orders', label: 'Work Orders', icon: '🔧', count: workOrders.length },
    { id: 'invoices', label: 'Invoices', icon: '📄', count: invoices.length },
    { id: 'payments', label: 'Payments', icon: '💳', count: transactions.length },
    { id: 'profile', label: 'Profile', icon: '👤', count: null },
  ];

  var bizType = (business?.business_type || '').toLowerCase();
  var isShop = bizType.includes('motorcycle') || bizType.includes('auto') || bizType.includes('repair');
  if (!isShop && workOrders.length === 0) {
    tabs = tabs.filter(function(t) { return t.id !== 'work-orders'; });
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      {/* HEADER */}
      <div style={{ background: '#fff', borderBottom: '1px solid #eee', padding: '16px 20px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 800, color: '#333', margin: 0 }}>{business?.business_name || 'Portal'}</h1>
            <p style={{ fontSize: 13, color: '#888', margin: 0 }}>Welcome, {contact.first_name}!</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Link to={'/book/' + subdomain} style={{ padding: '8px 18px', background: accent, color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>Book Now</Link>
            <button onClick={logout} style={{ padding: '8px 14px', background: '#f5f5f7', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#888', cursor: 'pointer' }}>Sign Out</button>
          </div>
        </div>
      </div>

      {/* TABS */}
      <div style={{ background: '#fff', borderBottom: '1px solid #eee', overflow: 'auto' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', gap: 0 }}>
          {tabs.map(function(t) {
            var active = tab === t.id;
            return (
              <button key={t.id} onClick={function() { setTab(t.id); }}
                style={{ padding: '14px 20px', background: 'none', border: 'none', borderBottom: active ? '3px solid ' + accent : '3px solid transparent',
                  color: active ? '#333' : '#888', fontSize: 14, fontWeight: active ? 700 : 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                <span>{t.icon}</span>
                {t.label}
                {t.count !== null && t.count > 0 && (
                  <span style={{ background: active ? accent + '15' : '#f0f0f2', color: active ? accent : '#888', fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 10 }}>{t.count}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* CONTENT */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 20px' }}>

        {/* APPOINTMENTS */}
        {tab === 'appointments' && (
          <div>
            {appointments.upcoming.length > 0 && (
              <div style={{ marginBottom: 32 }}>
                <h2 style={{ fontSize: 16, fontWeight: 700, color: '#333', marginBottom: 12 }}>Upcoming</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {appointments.upcoming.map(function(b) {
                    return (
                      <div key={b.id} style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', border: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: '#333' }}>{b.services?.name || 'Service'}</div>
                          <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                            {formatDate(b.booking_date)} at {formatTime(b.start_time)}
                            {b.staff && <span> · with {b.staff.first_name}</span>}
                          </div>
                        </div>
                        <span style={{ padding: '5px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: statusColor(b.status) + '15', color: statusColor(b.status), textTransform: 'capitalize' }}>{b.status}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {appointments.upcoming.length === 0 && (
              <div style={{ textAlign: 'center', padding: '48px 20px' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>📅</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: '#333', margin: '0 0 4px' }}>No upcoming appointments</h3>
                <p style={{ color: '#888', fontSize: 14, margin: '0 0 20px' }}>Ready to book your next visit?</p>
                <Link to={'/book/' + subdomain} style={{ padding: '12px 28px', background: accent, color: '#fff', borderRadius: 10, fontSize: 14, fontWeight: 700, textDecoration: 'none', display: 'inline-block' }}>Book Appointment</Link>
              </div>
            )}

            {appointments.past.length > 0 && (
              <div>
                <h2 style={{ fontSize: 16, fontWeight: 700, color: '#333', marginBottom: 12 }}>Past Appointments</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {appointments.past.slice(0, 10).map(function(b) {
                    return (
                      <div key={b.id} style={{ background: '#fff', borderRadius: 12, padding: '14px 18px', border: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: 0.75 }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#555' }}>{b.services?.name || 'Service'}</div>
                          <div style={{ fontSize: 12, color: '#aaa' }}>{formatDate(b.booking_date)}</div>
                        </div>
                        <span style={{ fontSize: 12, color: statusColor(b.status), fontWeight: 600, textTransform: 'capitalize' }}>{b.status}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* WORK ORDERS */}
        {tab === 'work-orders' && (
          <div>
            {workOrders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 20px' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🔧</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: '#333', margin: '0 0 4px' }}>No work orders</h3>
                <p style={{ color: '#888', fontSize: 14 }}>You don't have any work orders yet.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {workOrders.map(function(wo) {
                  return (
                    <div key={wo.id} style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', border: '1px solid #eee' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: '#333' }}>{wo.title || 'Work Order'}</div>
                          <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>#{wo.order_number}</div>
                        </div>
                        <span style={{ padding: '5px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: statusColor(wo.status) + '15', color: statusColor(wo.status), textTransform: 'capitalize' }}>{(wo.status || '').replace('_', ' ')}</span>
                      </div>
                      {wo.vehicles && (
                        <div style={{ fontSize: 13, color: '#666' }}>🏍️ {wo.vehicles.year} {wo.vehicles.make} {wo.vehicles.model}</div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                        <div style={{ fontSize: 12, color: '#aaa' }}>Created {new Date(wo.created_at).toLocaleDateString()}</div>
                        {wo.order_number && (
                          <Link to={'/track/' + wo.order_number} style={{ fontSize: 12, fontWeight: 700, color: accent, textDecoration: 'none' }}>Track Status →</Link>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* INVOICES */}
        {tab === 'invoices' && (
          <div>
            {invoices.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 20px' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>📄</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: '#333', margin: '0 0 4px' }}>No invoices</h3>
                <p style={{ color: '#888', fontSize: 14 }}>No invoices on file.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {invoices.map(function(inv) {
                  var balance = Number(inv.total || 0) - Number(inv.paid_amount || 0);
                  return (
                    <div key={inv.id} style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', border: '1px solid #eee' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: '#333' }}>Invoice #{inv.invoice_number}</div>
                          {inv.work_orders && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{inv.work_orders.title}</div>}
                        </div>
                        <span style={{ padding: '5px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: statusColor(inv.status) + '15', color: statusColor(inv.status), textTransform: 'capitalize' }}>{inv.status}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                        <div>
                          <span style={{ fontSize: 20, fontWeight: 800, color: '#333' }}>{formatCurrency(inv.total)}</span>
                          {balance > 0 && <span style={{ fontSize: 13, color: '#e74c3c', marginLeft: 8 }}>({formatCurrency(balance)} due)</span>}
                        </div>
                        <div style={{ fontSize: 12, color: '#aaa' }}>{new Date(inv.created_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* PAYMENTS */}
        {tab === 'payments' && (
          <div>
            {transactions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 20px' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>💳</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: '#333', margin: '0 0 4px' }}>No payments</h3>
                <p style={{ color: '#888', fontSize: 14 }}>No payment history yet.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {transactions.map(function(tx) {
                  return (
                    <div key={tx.id} style={{ background: '#fff', borderRadius: 12, padding: '14px 18px', border: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>{tx.services?.name || 'Payment'}</div>
                        <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>
                          {new Date(tx.created_at).toLocaleDateString()}
                          {tx.staff && <span> · {tx.staff.first_name}</span>}
                          {tx.payment_method && <span> · {tx.payment_method}</span>}
                        </div>
                      </div>
                      <span style={{ fontSize: 16, fontWeight: 700, color: '#00b894' }}>{formatCurrency(tx.total)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* PROFILE */}
        {tab === 'profile' && (
          <div>
            <div style={{ background: '#fff', borderRadius: 16, padding: '28px 24px', border: '1px solid #eee' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#333', margin: 0 }}>My Profile</h2>
                {!editing && (
                  <button onClick={function() { setEditing(true); }} style={{ padding: '8px 18px', background: accent + '10', color: accent, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Edit</button>
                )}
              </div>

              {!editing ? (
                <div style={{ display: 'grid', gap: 16 }}>
                  {[
                    { label: 'Name', value: (contact.first_name || '') + ' ' + (contact.last_name || '') },
                    { label: 'Email', value: contact.email || '—' },
                    { label: 'Phone', value: contact.phone || '—' },
                    { label: 'Member Since', value: new Date(contact.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) },
                  ].map(function(f, i) {
                    return (
                      <div key={i}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#888', marginBottom: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>{f.label}</div>
                        <div style={{ fontSize: 15, color: '#333', fontWeight: 500 }}>{f.value}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <form onSubmit={updateProfile} style={{ display: 'grid', gap: 14 }}>
                  {[
                    { key: 'first_name', label: 'First Name' },
                    { key: 'last_name', label: 'Last Name' },
                    { key: 'email', label: 'Email', type: 'email' },
                    { key: 'phone', label: 'Phone', type: 'tel' },
                  ].map(function(field) {
                    return (
                      <div key={field.key}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#888', marginBottom: 4 }}>{field.label}</label>
                        <input type={field.type || 'text'} value={profileForm[field.key] || ''}
                          onChange={function(e) { setProfileForm(Object.assign({}, profileForm, { [field.key]: e.target.value })); }}
                          style={{ width: '100%', padding: '12px 14px', border: '2px solid #e8e8ee', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                      </div>
                    );
                  })}
                  <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                    <button type="submit" style={{ padding: '12px 24px', background: accent, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Save</button>
                    <button type="button" onClick={function() { setEditing(false); setProfileForm({ first_name: contact.first_name, last_name: contact.last_name, phone: contact.phone || '', email: contact.email || '' }); }}
                      style={{ padding: '12px 24px', background: '#f5f5f7', color: '#888', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                  </div>
                </form>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 20 }}>
              {[
                { label: 'Total Visits', value: appointments.upcoming.length + appointments.past.length, icon: '📅' },
                { label: 'Total Spent', value: formatCurrency(transactions.reduce(function(s, t) { return s + Number(t.total || 0); }, 0)), icon: '💰' },
                { label: 'Avg per Visit', value: transactions.length > 0 ? formatCurrency(transactions.reduce(function(s, t) { return s + Number(t.total || 0); }, 0) / transactions.length) : '$0', icon: '📊' },
              ].map(function(s, i) {
                return (
                  <div key={i} style={{ background: '#fff', borderRadius: 14, padding: '20px 16px', border: '1px solid #eee', textAlign: 'center' }}>
                    <div style={{ fontSize: 24, marginBottom: 6 }}>{s.icon}</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#333' }}>{s.value}</div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{s.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomerPortal;
